import React, { useState, useEffect } from 'react';
import DocumentLayout from './DocumentLayout';
import LegalNavigation from './LegalNavigation';
import LegalBindingNotice from './LegalBindingNotice';
import ContractParties from './ContractParties';
import { typography, spacing, colors, interactive } from './legalStyles';
import { getCurrentLegalPage } from './legalPagesConfig';
import { useTranslation } from 'utils/i18n';
import { useDarkMode } from 'utils/darkMode';
import logger from 'utils/logger';

/**
 * LegalLayout
 * Legal document layout with navigation and binding notice.
 * Uses DocumentLayout as base with legal-specific customizations.
 * Props:
 * - title: string
 * - lastUpdated: string
 * - sections: Array<{ id, title, content (markdown|string), children?: [] }>
 */
const LegalLayout = ({ title, lastUpdated, sections = [] }) => {
  const { language } = useTranslation();
  const { isDarkMode } = useDarkMode();
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailAddress, setEmailAddress] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailError, setEmailError] = useState(null);
  const [emailSuccess, setEmailSuccess] = useState(false);
  
  // Try to get user info on mount (for personalized PDF)
  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const { getOrSelectActiveAccount } = await import('utils/msalConfig');
        const account = await getOrSelectActiveAccount();
        if (account) {
          setUserInfo({
            displayName: account.name || account.username,
            email: account.username
          });
        }
      } catch (e) {
        // User not authenticated - that's fine
        setUserInfo(null);
      }
    };
    fetchUserInfo();
  }, []);
  
  // Map page IDs to document types for PDF download
  // All legal pages should show the button, but only some have PDF support
  const getDocumentTypeFromPath = () => {
    const currentPath = window.location.pathname;
    const currentPage = getCurrentLegalPage(currentPath);
    if (!currentPage) return null;
    
    // Map page IDs to document types
    const docTypeMap = {
      'terms': 'terms',
      'privacy': 'privacy',
      'dpa': 'dpa',
      'eula': 'eula',
      'business-dpa': 'business-dpa',
      'acceptable-use': 'acceptable-use',
      'disclaimer': 'disclaimer',
      'impressum': 'impressum',
      'cookies': 'cookies',
      'gdpr': 'gdpr'
    };
    
    return docTypeMap[currentPage.id] || currentPage.id;
  };
  
  const handleDownloadPDF = async () => {
    const docType = getDocumentTypeFromPath();
    if (!docType) {
      setDownloadError('Document type not found');
      return;
    }
    
    setDownloading(true);
    setDownloadError(null);
    
    try {
      // Get API endpoint from centralized config
      const { getApiEndpoint } = await import('utils/centralizedConfig');
      const baseUrl = await getApiEndpoint('customer-portal');
      
      // Build URL with optional user info
      const params = new URLSearchParams({ language });
      if (userInfo?.displayName) {
        params.append('downloaded_by', userInfo.displayName);
      }
      
      // Check if baseUrl already has /api prefix to avoid double /api/api/
      const hasApiPrefix = baseUrl && (baseUrl.endsWith('/api') || baseUrl.includes('/api/'));
      const endpointPath = `/v1/legal/pdf/${docType}?${params.toString()}`;
      const apiUrl = hasApiPrefix 
        ? `${baseUrl}${endpointPath}`  // baseUrl already has /api
        : `${baseUrl}/api${endpointPath}`;  // baseUrl doesn't have /api (localhost)
      
      // Try to get auth token if available (but don't require it)
      let headers = {};
      try {
        const { getOrSelectActiveAccount } = await import('utils/msalConfig');
        const account = await getOrSelectActiveAccount();
        if (account) {
          const { PublicClientApplication } = await import('@azure/msal-browser');
          const { msalConfig } = await import('utils/msalConfig');
          const msalInstance = new PublicClientApplication(msalConfig);
          await msalInstance.initialize();
          
          const tokenResponse = await msalInstance.acquireTokenSilent({
            scopes: ['openid', 'profile', 'User.Read'],
            account: account
          });
          headers['Authorization'] = `Bearer ${tokenResponse.accessToken}`;
        }
      } catch (authError) {
        // Authentication failed - continue without auth (anonymous download)
        logger.debug('LEGAL-LAYOUT', 'Downloading PDF without authentication');
      }
      
      // Download PDF (works for both authenticated and anonymous users)
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers,
      });
      
      if (!response.ok) {
        if (response.status === 400) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || 'PDF not available for this document yet.');
        }
        throw new Error(`Failed to download PDF: ${response.statusText}`);
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${docType}_${language}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      logger.error('LEGAL-LAYOUT', 'Error downloading PDF', { error: error.message });
      setDownloadError(error.message || 'Failed to download PDF. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const handleSendEmail = async () => {
    const docType = getDocumentTypeFromPath();
    if (!docType) {
      setEmailError('Document type not found');
      return;
    }

    // Validate email
    if (!emailAddress || !emailAddress.includes('@')) {
      setEmailError('Please enter a valid email address');
      return;
    }

    setSendingEmail(true);
    setEmailError(null);
    setEmailSuccess(false);

    try {
      // Get API endpoint from centralized config
      const { getApiEndpoint } = await import('utils/centralizedConfig');
      const baseUrl = await getApiEndpoint('customer-portal');
      
      // Check if baseUrl already has /api prefix to avoid double /api/api/
      const hasApiPrefix = baseUrl && (baseUrl.endsWith('/api') || baseUrl.includes('/api/'));
      const endpointPath = '/v1/legal/pdf/email';
      const apiUrl = hasApiPrefix 
        ? `${baseUrl}${endpointPath}`  // baseUrl already has /api
        : `${baseUrl}/api${endpointPath}`;  // baseUrl doesn't have /api (localhost)
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          document_type: docType,
          language: language,
          email: emailAddress
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to send email: ${response.statusText}`);
      }

      const result = await response.json();
      if (result.success) {
        setEmailSuccess(true);
        setTimeout(() => {
          setShowEmailModal(false);
          setEmailAddress('');
          setEmailSuccess(false);
        }, 2000);
      } else {
        throw new Error(result.error || 'Failed to send email');
      }
    } catch (error) {
      logger.error('LEGAL-LAYOUT', 'Error sending email', { error: error.message });
      setEmailError(error.message || 'Failed to send email. Please try again.');
    } finally {
      setSendingEmail(false);
    }
  };
  
  const currentPage = getCurrentLegalPage(window.location.pathname);
  const showDownloadButton = currentPage !== null;
  const titleStyle = {
    ...typography.h2,
    fontSize: '16px',
    margin: `0 0 ${spacing.sm} 0`,
    color: isDarkMode ? '#e5e7eb' : colors.text
  };

  const metaStyle = {
    ...typography.small,
    margin: `0 0 ${spacing.md} 0`,
    color: isDarkMode ? '#94a3b8' : colors.muted
  };

  // Button styling matching legal page design system
  const buttonStyle = {
    ...interactive.button,
    padding: `${spacing.sm} ${spacing.md}`,
    fontSize: typography.small.fontSize,
    fontWeight: 500,
    color: isDarkMode ? '#e5e7eb' : colors.text,
    background: isDarkMode ? '#242831' : colors.background,
    borderColor: isDarkMode ? '#3a4553' : colors.border,
    display: 'inline-flex',
    alignItems: 'center',
    gap: spacing.xs,
    cursor: downloading ? 'not-allowed' : 'pointer',
    opacity: downloading ? 0.6 : 1,
    transition: 'all 0.2s ease',
  };

  // Render sidebar top with navigation and title/meta (NO download button here)
  const renderSidebarTop = () => (
    <>
      {/* Desktop Vertical Tab Navigation */}
      <LegalNavigation mobileOnly={false} />
      
      {/* Document Title and Meta */}
      <div style={{ marginBottom: spacing.sm, marginTop: 0 }}>
        <h2 style={titleStyle}>{title}</h2>
        {lastUpdated ? (
          <div style={{ ...metaStyle, marginBottom: 0 }}>Last updated: {lastUpdated}</div>
        ) : null}
      </div>
    </>
  );

  // Render custom content header with title and download button on the right
  const renderContentHeader = () => (
    <header style={{ 
      display: 'flex', 
      justifyContent: 'space-between', 
      alignItems: 'flex-start',
      marginBottom: spacing.md,
      marginTop: 0,
      paddingTop: 0,
      flexWrap: 'wrap',
      gap: spacing.sm
    }}>
      <div>
        <h1 style={{ ...typography.h1, margin: `0 0 ${spacing.sm} 0`, paddingTop: 0, color: isDarkMode ? '#e5e7eb' : colors.text }}>{title}</h1>
        {lastUpdated && (
          <p style={{ ...typography.small, margin: 0, color: isDarkMode ? '#94a3b8' : colors.muted }}>Last updated: {lastUpdated}</p>
        )}
      </div>
      {showDownloadButton && (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: spacing.xs }}>
          <div style={{ display: 'flex', gap: spacing.xs, alignItems: 'center' }}>
            <button
              onClick={handleDownloadPDF}
              disabled={downloading}
              style={buttonStyle}
              onMouseEnter={(e) => {
                if (!downloading) {
                  e.target.style.background = isDarkMode ? '#2d3748' : colors.backgroundHover;
                  e.target.style.borderColor = isDarkMode ? '#3a4553' : colors.border;
                }
              }}
              onMouseLeave={(e) => {
                if (!downloading) {
                  e.target.style.background = isDarkMode ? '#242831' : colors.background;
                  e.target.style.borderColor = isDarkMode ? '#3a4553' : colors.border;
                }
              }}
            >
              {downloading ? 'Downloading...' : 'Download PDF'}
            </button>
            <button
              onClick={() => setShowEmailModal(true)}
              disabled={downloading}
              style={buttonStyle}
              onMouseEnter={(e) => {
                if (!downloading) {
                  e.target.style.background = isDarkMode ? '#2d3748' : colors.backgroundHover;
                  e.target.style.borderColor = isDarkMode ? '#3a4553' : colors.border;
                }
              }}
              onMouseLeave={(e) => {
                if (!downloading) {
                  e.target.style.background = isDarkMode ? '#242831' : colors.background;
                  e.target.style.borderColor = isDarkMode ? '#3a4553' : colors.border;
                }
              }}
            >
              Send to Email
            </button>
          </div>
          {downloadError && (
            <div style={{
              ...typography.small,
              color: '#dc2626',
              fontSize: '11px',
              textAlign: 'right'
            }}>
              {downloadError}
            </div>
          )}
        </div>
      )}
    </header>
  );

  // Render content before sections (contract parties at top, then binding notice)
  const renderContentBeforeSections = () => (
    <>
      <ContractParties />
      <LegalBindingNotice />
    </>
  );

  // Email modal component
  const renderEmailModal = () => {
    if (!showEmailModal) return null;

    const modalOverlayStyle = {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    };

    const modalContentStyle = {
      background: isDarkMode ? '#1f2937' : '#ffffff',
      borderRadius: '8px',
      padding: spacing.lg,
      maxWidth: '400px',
      width: '90%',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    };

    const inputStyle = {
      width: '100%',
      padding: spacing.sm,
      marginBottom: spacing.sm,
      border: `1px solid ${isDarkMode ? '#3a4553' : colors.border}`,
      borderRadius: '4px',
      background: isDarkMode ? '#242831' : '#ffffff',
      color: isDarkMode ? '#e5e7eb' : colors.text,
      fontSize: typography.small.fontSize,
    };

    return (
      <div style={modalOverlayStyle} onClick={() => !sendingEmail && setShowEmailModal(false)}>
        <div style={modalContentStyle} onClick={(e) => e.stopPropagation()}>
          <h3 style={{ ...typography.h3, margin: `0 0 ${spacing.md} 0`, color: isDarkMode ? '#e5e7eb' : colors.text }}>
            Send PDF to Email
          </h3>
          <p style={{ ...typography.small, marginBottom: spacing.md, color: isDarkMode ? '#94a3b8' : colors.muted }}>
            Enter your email address to receive a copy of this document.
          </p>
          <input
            type="email"
            placeholder="your.email@example.com"
            value={emailAddress}
            onChange={(e) => {
              setEmailAddress(e.target.value);
              setEmailError(null);
            }}
            style={inputStyle}
            disabled={sendingEmail}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !sendingEmail) {
                handleSendEmail();
              }
            }}
          />
          {emailError && (
            <div style={{
              ...typography.small,
              color: '#dc2626',
              marginBottom: spacing.sm,
            }}>
              {emailError}
            </div>
          )}
          {emailSuccess && (
            <div style={{
              ...typography.small,
              color: '#10b981',
              marginBottom: spacing.sm,
            }}>
              PDF sent successfully!
            </div>
          )}
          <div style={{ display: 'flex', gap: spacing.sm, justifyContent: 'flex-end' }}>
            <button
              onClick={() => {
                setShowEmailModal(false);
                setEmailAddress('');
                setEmailError(null);
                setEmailSuccess(false);
              }}
              disabled={sendingEmail}
              style={{
                ...buttonStyle,
                opacity: sendingEmail ? 0.5 : 1,
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSendEmail}
              disabled={sendingEmail || !emailAddress}
              style={{
                ...buttonStyle,
                opacity: (sendingEmail || !emailAddress) ? 0.5 : 1,
                background: isDarkMode ? '#059669' : '#10b981',
                color: '#ffffff',
              }}
            >
              {sendingEmail ? 'Sending...' : 'Send'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Mobile Dropdown Navigation - At top of page */}
      <div className="legal-navigation-mobile-top">
        <LegalNavigation mobileOnly={true} />
      </div>
      
      <DocumentLayout
        title={title}
        lastUpdated={lastUpdated}
        sections={sections}
        renderSidebarTop={renderSidebarTop}
        renderContentHeader={renderContentHeader}
        renderContentBeforeSections={renderContentBeforeSections}
        sidebarAriaLabel="Document navigation"
      />
      
      {renderEmailModal()}
    </>
  );
};

export default LegalLayout;
